import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import FeatureGate from "@/components/FeatureGate";

const REPORTS = [
  { key: "weekly", label: "Weekly Digest", desc: "Top opportunities from the past 7 days" },
  { key: "market", label: "Market Report", desc: "Category-level activity across all US states" },
  { key: "state", label: "State Report", desc: "State-level distress distribution" },
  { key: "closures", label: "Business Closures", desc: "All active business closures & liquidations" },
  { key: "foreclosures", label: "Foreclosure Report", desc: "Residential + commercial foreclosures nationwide" },
  { key: "vacancy", label: "Commercial Vacancy", desc: "Vacant commercial + industrial properties" },
];

export default function ReportsPage() {
  return (
    <FeatureGate feature="exports" title="Reports & Exports are premium" blurb="Weekly digests, market reports, and multi-format exports (CSV, Excel, JSON, PDF). Available on all paid tiers.">
      <ReportsInner />
    </FeatureGate>
  );
}

function ReportsInner() {
  const [format, setFormat] = useState("csv");

  const download = async (reportKey) => {
    try {
      const catMap = {
        closures: "closing,liquidation", foreclosures: "foreclosure",
        vacancy: "vacant", state: "", market: "", weekly: "",
      };
      const cat = catMap[reportKey];
      const params = new URLSearchParams();
      if (cat) params.set("category", cat);
      params.set("limit", "500");
      const r = await api.get(`/opportunities?${params.toString()}`);
      const rows = r.data.items || [];
      if (format === "json") {
        downloadFile(`${reportKey}-report.json`, JSON.stringify(rows, null, 2), "application/json");
      } else if (format === "csv" || format === "excel") {
        const cols = ["id", "category", "title", "city", "state", "industry", "building_size_sqft", "estimated_value", "price", "auction_date", "ai_score.opportunity"];
        const header = cols.join(",");
        const csvRows = rows.map(r => cols.map(c => {
          const val = c.includes(".") ? c.split(".").reduce((o, k) => o?.[k], r) : r[c];
          return JSON.stringify(val ?? "");
        }).join(","));
        const csv = [header, ...csvRows].join("\n");
        downloadFile(`${reportKey}-report.csv`, csv, "text/csv");
      } else {
        // PDF: simple print-friendly HTML
        const w = window.open("", "_blank");
        w.document.write(`<h1>${reportKey.toUpperCase()} Report</h1><table border=1 cellpadding=6><tr>${["Title","City","State","Score","Price"].map(h=>`<th>${h}</th>`).join("")}</tr>${rows.map(r=>`<tr><td>${r.title}</td><td>${r.city}</td><td>${r.state}</td><td>${Math.round(r.ai_score?.opportunity||0)}</td><td>$${(r.price||0).toLocaleString()}</td></tr>`).join("")}</table>`);
        setTimeout(() => w.print(), 500);
      }
      toast.success(`${reportKey} report downloaded`);
    } catch { toast.error("Report generation failed"); }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Reports</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Generate & export intelligence.</h1>
      </div>
      <div className="flex items-center gap-2 text-[12px]">
        <span className="text-[#7a6d5a] uppercase tracking-wider">Format:</span>
        {["csv", "excel", "json", "pdf"].map((f) => (
          <button key={f} onClick={() => setFormat(f)} data-testid={`format-${f}`} className={`px-3 py-1 rounded border uppercase tracking-wider ${format === f ? "bg-[#d4a574] text-[#14100c] border-[#d4a574]" : "border-[#33291f] text-[#b8ac96] hover:border-[#d4a574]"}`}>{f}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {REPORTS.map((r) => (
          <div key={r.key} className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712] hover:border-[#d4a574]/40">
            <FileText size={16} className="text-[#d4a574] mb-3" />
            <div className="font-display text-lg font-semibold mb-1">{r.label}</div>
            <div className="text-[12px] text-[#b8ac96] mb-4">{r.desc}</div>
            <button onClick={() => download(r.key)} data-testid={`report-${r.key}`} className="flex items-center gap-2 px-3 py-2 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[12px] text-[#d4a574]">
              <Download size={13} /> Download .{format}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function downloadFile(name, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}
